import { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Pencil, Trash2, Plus, Users, Target, Zap, Calendar, ChevronRight, Search,
  CheckSquare, ClipboardList,
} from 'lucide-react';

import { CategoryBadge } from '@/components/shared/CategoryBadge';
import { PriorityBadge } from '@/components/shared/PriorityBadge';
import { RoleBadge } from '@/components/shared/RoleBadge';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ProgressBar } from '@/components/shared/ProgressBar';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { GroupMembersDialog } from '@/components/groups/GroupMembersDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import {
  groupService, GROUP_CATEGORIES,
  type GroupMember,
} from '@/services/group.service';
import { goalService, GOAL_PRIORITIES, type Goal } from '@/services/goal.service';
import { sprintService } from '@/services/sprint.service';
import { taskService } from '@/services/task.service';

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------
const editSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(2000).optional(),
  category: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
});
type EditFormData = z.infer<typeof editSchema>;

// addMemberSchema no longer needed - using autocomplete inline

// ---------------------------------------------------------------------------
// Detail Page
// ---------------------------------------------------------------------------
export function GroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromTab = (location.state as any)?.fromTab;
  const backUrl = fromTab ? `/work?tab=${fromTab}` : '/groups';
  const backLabel = fromTab ? 'Back to Work Planning' : 'Back to Groups';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: group, isLoading } = useQuery({
    queryKey: ['group', id],
    queryFn: () => groupService.get(id!).then((r) => r.data.data),
    enabled: !!id,
  });

  // Fetch goals, sprints, tasks for computed stats
  const { data: goalsData } = useQuery({
    queryKey: ['group-goals-stats', id],
    queryFn: () => goalService.list({ group_id: id!, limit: 100 }).then((r) => r.data.data),
    enabled: !!id,
  });
  const { data: sprintsData } = useQuery({
    queryKey: ['group-sprints-stats', id],
    queryFn: () => sprintService.list({ group_id: id!, limit: 100 }).then((r) => r.data.data),
    enabled: !!id,
  });
  const { data: tasksData } = useQuery({
    queryKey: ['group-tasks-stats', id],
    queryFn: () => taskService.list({ group_id: id!, limit: 100 }).then((r) => r.data.data),
    enabled: !!id,
  });

  // Edit
  const [editOpen, setEditOpen] = useState(false);
  const editForm = useForm<EditFormData>({ resolver: zodResolver(editSchema) });
  const [editLoading, setEditLoading] = useState(false);

  function openEdit() {
    if (!group) return;
    editForm.reset({
      name: group.name, description: group.description ?? '',
      category: group.category, start_date: group.start_date ?? '', end_date: group.end_date ?? '',
    });
    setEditOpen(true);
  }

  const editMutation = useMutation({
    mutationFn: (formData: EditFormData) => groupService.update(id!, {
      name: formData.name, description: formData.description || null,
      category: formData.category || undefined,
      start_date: formData.start_date || null, end_date: formData.end_date || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', id] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({ title: 'Group updated' }); setEditOpen(false);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Update failed', variant: 'destructive' }),
    onSettled: () => setEditLoading(false),
  });

  // Toggle active
  const toggleMutation = useMutation({
    mutationFn: (isActive: boolean) => groupService.update(id!, { is_active: isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', id] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({ title: 'Group status updated' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  // Delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: () => groupService.delete(id!),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['groups'] }); toast({ title: 'Group deleted' }); navigate(backUrl); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Delete failed', variant: 'destructive' }),
    onSettled: () => setDeleteLoading(false),
  });

  // Members dialog
  const [membersOpen, setMembersOpen] = useState(false);

  // Stats dialog
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [statsType, setStatsType] = useState<'goals' | 'sprints' | 'tasks' | null>(null);
  const [statsFilter, setStatsFilter] = useState<'total' | 'completed' | 'late' | null>(null);

  if (isLoading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full" /></div>;
  }

  if (!group) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-2">Group Not Found</h2>
        <p className="text-muted-foreground mb-4">The group you're looking for doesn't exist or you don't have access.</p>
        <Button onClick={() => navigate(backUrl)}>{backLabel}</Button>
      </div>
    );
  }

  const stats = group.stats ?? { goals_count: 0, sprints_count: 0, members_count: 0 };
  const userGroupRole = group.current_user_role;
  // Who can edit/delete: group owner OR workspace admin/owner (backend returns 'owner' for those)
  const canEdit = userGroupRole === 'owner';
  // Who can manage members + create goals: group owner OR moderator
  const canManage = userGroupRole === 'owner' || userGroupRole === 'moderator';

  // Compute detailed stats from fetched data
  const now = new Date();
  const goals = goalsData ?? [];
  const sprints = sprintsData ?? [];
  const tasks = tasksData ?? [];

  const completedGoals = goals.filter((g: any) => g.status === 'completed' || g.status === 'completed_late').length;
  const lateGoals = goals.filter((g: any) => g.status === 'active' && g.due_date && new Date(g.due_date) < now).length;
  const completedSprints = sprints.filter((s: any) => s.status === 'completed').length;
  const lateSprints = sprints.filter((s: any) => s.status === 'active' && s.end_date && new Date(s.end_date) < now).length;
  const completedTasks = tasks.filter((t: any) => t.phase_status === 'done').length;
  const lateTasks = tasks.filter((t: any) => t.calculated_status === 'late' || t.calculated_status === 'completed_late').length;

  function handleStatClick(type: 'goals' | 'sprints' | 'tasks', filter: 'total' | 'completed' | 'late') {
    setStatsType(type);
    setStatsFilter(filter);
    setStatsDialogOpen(true);
  }

  function getStatsItems() {
    if (!statsType || !statsFilter) return [];
    if (statsType === 'goals') {
      if (statsFilter === 'total') return goals;
      if (statsFilter === 'completed') return goals.filter((g: any) => g.status === 'completed' || g.status === 'completed_late');
      return goals.filter((g: any) => g.status === 'active' && g.due_date && new Date(g.due_date) < now);
    }
    if (statsType === 'sprints') {
      if (statsFilter === 'total') return sprints;
      if (statsFilter === 'completed') return sprints.filter((s: any) => s.status === 'completed');
      return sprints.filter((s: any) => s.status === 'active' && s.end_date && new Date(s.end_date) < now);
    }
    if (statsFilter === 'total') return tasks;
    if (statsFilter === 'completed') return tasks.filter((t: any) => t.phase_status === 'done');
    return tasks.filter((t: any) => t.calculated_status === 'late' || t.calculated_status === 'completed_late');
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to="/groups" className="hover:text-foreground transition-colors">Groups</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="font-medium text-foreground">{group.name}</span>
      </nav>

      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-3xl">{group.name}</CardTitle>
                <CategoryBadge category={group.category} />
                <Badge variant="outline" className="font-mono text-xs">{group.reference_id}</Badge>
                {!group.is_active && <Badge variant="outline" className="bg-muted">Inactive</Badge>}
              </div>
              <CardDescription>{group.description || 'No description provided'}</CardDescription>
            </div>
            <div className="flex gap-2">
              {canEdit && (
                <>
                  <Button variant="outline" size="sm" onClick={openEdit}>
                    <Pencil className="h-4 w-4 mr-2" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleMutation.mutate(!group.is_active)}
                  >
                    {group.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              {group.owner && (
                <>
                  <UserAvatar name={group.owner.full_name} avatarUrl={group.owner.avatar_url} size="md" />
                  <div>
                    <p className="text-sm text-muted-foreground">Owner</p>
                    <p className="font-medium">{group.owner.full_name}</p>
                  </div>
                </>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>{stats.members_count} members</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>Created {new Date(group.created_at).toLocaleDateString()}</span>
              </div>
              {(group.start_date || group.end_date) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {group.start_date && group.end_date
                      ? `${new Date(group.start_date).toLocaleDateString()} to ${new Date(group.end_date).toLocaleDateString()}`
                      : group.start_date
                      ? `Started ${new Date(group.start_date).toLocaleDateString()}`
                      : `Ends ${new Date(group.end_date!).toLocaleDateString()}`}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Tasks Link */}
      {group.default_team_id && (
        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <ClipboardList className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium">Project Tasks</p>
                <p className="text-sm text-muted-foreground">Manage flat tasks for this project team</p>
              </div>
            </div>
            <Button onClick={() => navigate(`/teams/${group.default_team_id}`)}>View Tasks</Button>
          </CardContent>
        </Card>
      )}

      {/* Statistics Card */}
      <Card>
        <CardHeader>
          <CardTitle>Statistics</CardTitle>
          <CardDescription>Overview of goals, sprints, and tasks in this group</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <StatCell icon={Target} label="Total Goals" value={stats.goals_count} onClick={() => handleStatClick('goals', 'total')} />
            <StatCell icon={Target} label="Completed Goals" value={completedGoals} color="success" onClick={() => handleStatClick('goals', 'completed')} />
            <StatCell icon={Target} label="Late Goals" value={lateGoals} color="destructive" onClick={() => handleStatClick('goals', 'late')} />
            <StatCell icon={Zap} label="Total Sprints" value={stats.sprints_count} onClick={() => handleStatClick('sprints', 'total')} />
            <StatCell icon={Zap} label="Completed Sprints" value={completedSprints} color="success" onClick={() => handleStatClick('sprints', 'completed')} />
            <StatCell icon={Zap} label="Late Sprints" value={lateSprints} color="destructive" onClick={() => handleStatClick('sprints', 'late')} />
            <StatCell icon={CheckSquare} label="Total Tasks" value={tasks.length} onClick={() => handleStatClick('tasks', 'total')} />
            <StatCell icon={CheckSquare} label="Completed Tasks" value={completedTasks} color="success" onClick={() => handleStatClick('tasks', 'completed')} />
            <StatCell icon={CheckSquare} label="Late Tasks" value={lateTasks} color="destructive" onClick={() => handleStatClick('tasks', 'late')} />
          </div>
        </CardContent>
      </Card>

      {/* Members Section */}
      <MembersSection groupId={id!} membersCount={stats.members_count} canManage={canManage} onOpenDialog={() => setMembersOpen(true)} />

      {/* Goals Section */}
      <GoalsSection groupId={id!} canManage={canManage} />

      {/* Members Dialog */}
      <GroupMembersDialog open={membersOpen} onOpenChange={setMembersOpen} groupId={id!} groupName={group.name} canManage={canManage} canChangeRoles={canEdit} />

      {/* Statistics Dialog */}
      <StatsDialog
        open={statsDialogOpen}
        onOpenChange={setStatsDialogOpen}
        type={statsType}
        filter={statsFilter}
        items={getStatsItems()}
      />

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Edit Group</DialogTitle>
            <DialogDescription>Update group details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit((d) => { setEditLoading(true); editMutation.mutate(d); })} className="space-y-4">
            <div className="space-y-2"><Label>Name *</Label><Input {...editForm.register('name')} />{editForm.formState.errors.name && <p className="text-xs text-destructive">{editForm.formState.errors.name.message}</p>}</div>
            <div className="space-y-2"><Label>Description</Label><textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" {...editForm.register('description')} rows={3} /></div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={editForm.watch('category') || ''} onValueChange={(v) => editForm.setValue('category', v)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{GROUP_CATEGORIES.map((cat) => <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start Date</Label><Input type="date" {...editForm.register('start_date')} /></div>
              <div className="space-y-2"><Label>End Date</Label><Input type="date" {...editForm.register('end_date')} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={editLoading}>{editLoading ? 'Saving...' : 'Save Changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={deleteOpen} onOpenChange={setDeleteOpen} title="Are you sure?"
        description={`This will permanently delete the group "${group.name}" and all related goals, sprints, and tasks. This action cannot be undone.`}
        confirmLabel="Delete" variant="destructive" isLoading={deleteLoading}
        onConfirm={() => { setDeleteLoading(true); deleteMutation.mutate(); }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat Cell (clickable)
// ---------------------------------------------------------------------------
function StatCell({ icon: Icon, label, value, color, onClick }: {
  icon: React.ElementType; label: string; value: number; color?: string; onClick?: () => void;
}) {
  const iconColor = color === 'success' ? 'text-emerald-600' : color === 'destructive' ? 'text-destructive' : 'text-primary';
  const valueColor = color === 'destructive' ? 'text-destructive' : '';
  return (
    <div
      className="flex flex-col p-4 border rounded-lg cursor-pointer hover:bg-accent transition-colors"
      onClick={onClick}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-5 w-5 ${iconColor}`} />
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
      </div>
      <span className={`text-3xl font-bold ${valueColor}`}>{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Members Section (inline on detail page)
// ---------------------------------------------------------------------------
function MembersSection({ groupId, canManage, onOpenDialog }: { groupId: string; membersCount: number; canManage: boolean; onOpenDialog: () => void }) {
  const [memberSearch, setMemberSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['group-members', groupId],
    queryFn: () => groupService.listMembers(groupId, { limit: 50 }).then((r) => r.data),
  });

  const members: GroupMember[] = data?.data ?? [];
  const filtered = memberSearch
    ? members.filter((m) => m.user?.full_name?.toLowerCase().includes(memberSearch.toLowerCase()) || m.user?.email?.toLowerCase().includes(memberSearch.toLowerCase()))
    : members;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Members</CardTitle>
          {canManage && <Button onClick={onOpenDialog}>Members</Button>}
        </div>
        <div className="relative mt-4">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search members..." value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} className="pl-8" />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-14" />)}</div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No members found</p>
        ) : (
          <div className="space-y-3">
            {filtered.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <UserAvatar name={m.user?.full_name ?? 'Unknown'} avatarUrl={m.user?.avatar_url} size="sm" />
                  <div>
                    <p className="font-medium">{m.user?.full_name ?? 'Unknown'}</p>
                    <p className="text-sm text-muted-foreground">{m.user?.email ?? ''}</p>
                  </div>
                </div>
                <RoleBadge role={m.role} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Statistics Dialog (matching old system)
// ---------------------------------------------------------------------------
function StatsDialog({ open, onOpenChange, type, filter, items }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  type: 'goals' | 'sprints' | 'tasks' | null; filter: 'total' | 'completed' | 'late' | null;
  items: any[];
}) {
  const typeLabel = type === 'goals' ? 'Goals' : type === 'sprints' ? 'Sprints' : 'Tasks';
  const filterLabel = filter === 'total' ? 'All' : filter === 'completed' ? 'Completed' : 'Late';

  const statusColor: Record<string, string> = {
    completed: 'bg-emerald-100 text-emerald-800',
    completed_late: 'bg-amber-100 text-amber-800',
    active: 'bg-blue-100 text-blue-800',
    draft: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-gray-100 text-gray-600',
    done: 'bg-emerald-100 text-emerald-800',
    in_progress: 'bg-blue-100 text-blue-800',
    not_started: 'bg-gray-100 text-gray-600',
    planning: 'bg-gray-100 text-gray-600',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{filterLabel} {typeLabel}</DialogTitle>
          <DialogDescription>Showing {items.length} {type}</DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-2">
            {items.map((item: any) => (
              <Link key={item.id} to={type === 'goals' ? `/goals/${item.id}` : type === 'sprints' ? `/sprints/${item.id}` : `/tasks/${item.id}`} className="block">
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors">
                  <div className="flex-1 min-w-0">
                    <span className="font-medium truncate block">{item.title || item.name}</span>
                    {(item.due_date || item.end_date) && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {item.due_date ? `Due: ${new Date(item.due_date).toLocaleDateString()}` : `${new Date(item.start_date).toLocaleDateString()} - ${new Date(item.end_date).toLocaleDateString()}`}
                      </p>
                    )}
                  </div>
                  <Badge className={statusColor[item.status || item.phase_status] || 'bg-gray-100 text-gray-600'}>
                    {(item.status || item.phase_status || '').replace('_', ' ')}
                  </Badge>
                </div>
              </Link>
            ))}
            {items.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No items found</p>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Goals Section
// ---------------------------------------------------------------------------
function GoalsSection({ groupId, canManage }: { groupId: string; canManage: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [goalTitle, setGoalTitle] = useState('');
  const [goalPriority, setGoalPriority] = useState('medium');
  const [goalDueDate, setGoalDueDate] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['group-goals', groupId],
    queryFn: () => goalService.list({ group_id: groupId, limit: 20 }).then((r) => r.data),
  });

  const goals: Goal[] = data?.data ?? [];

  const createMut = useMutation({
    mutationFn: () => goalService.create({ title: goalTitle, priority: goalPriority, due_date: goalDueDate, group_id: groupId, owner_id: user!.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-goals', groupId] });
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['group-goals-stats', groupId] });
      toast({ title: 'Goal created' }); setCreateOpen(false);
      setGoalTitle(''); setGoalPriority('medium'); setGoalDueDate('');
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Goals ({goals.length})</h3>
        {canManage && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> Create Goal
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : goals.length === 0 ? (
        <EmptyState icon={Target} title="No goals yet" description="Create a goal to track objectives." />
      ) : (
        <div className="space-y-2">
          {goals.map((goal) => (
            <Link key={goal.id} to={`/goals/${goal.id}`} className="block">
              <Card className="p-4 transition-colors hover:bg-muted/50">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] text-muted-foreground">{goal.reference_id}</span>
                    <StatusBadge status={goal.status} />
                    <PriorityBadge priority={goal.priority} />
                  </div>
                  {goal.due_date && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                      <Calendar className="h-3 w-3" />{new Date(goal.due_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium truncate">{goal.title}</p>
                <div className="mt-2"><ProgressBar value={goal.progress_percentage ?? 0} size="sm" /></div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Create Goal</DialogTitle>
            <DialogDescription>Add a new goal to this group.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }} className="space-y-4">
            <div className="space-y-2"><Label>Title *</Label><Input value={goalTitle} onChange={(e) => setGoalTitle(e.target.value)} placeholder="Goal title" required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={goalPriority} onValueChange={setGoalPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{GOAL_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Due Date *</Label><Input type="date" value={goalDueDate} onChange={(e) => setGoalDueDate(e.target.value)} required /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!goalTitle.trim() || !goalDueDate}>Create Goal</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
